import { z } from "zod"
import configuration from "./evm-chains.json"

export const gachaEvmAddressSchema = z
  .string()
  .regex(/^0x[0-9a-fA-F]{40}$/)
  .transform((value): `0x${string}` => `0x${value.slice(2)}`)
const tokenSchema = z.object({
  key: z.string().min(1),
  symbol: z.string().min(1),
  icon: z.string().startsWith("/images/currencies/").optional(),
  address: gachaEvmAddressSchema,
  decimals: z.number().int().min(0).max(255),
})
const gachaEvmIconSchema = z.string().startsWith("/images/blockchains/")

export const gachaEvmChainSchema = z
  .object({
    chainId: z.number().int().positive(),
    chainKey: z.string().min(1),
    name: z.string().min(1),
    icon: gachaEvmIconSchema.optional(),
    nativeCurrency: z.object({
      name: z.string(),
      symbol: z.string(),
      decimals: z.number().int(),
    }),
    rpcUrl: z.string().url(),
    explorerUrl: z.string().url(),
    paymentContract: gachaEvmAddressSchema,
    vaultContract: gachaEvmAddressSchema,
    cardContract: gachaEvmAddressSchema,
    mintGuardContract: gachaEvmAddressSchema,
    preferredToken: z.string(),
    tokens: z.array(tokenSchema).min(1),
    ethPayment: z
      .object({
        weth: gachaEvmAddressSchema,
        router: gachaEvmAddressSchema,
        quoter: gachaEvmAddressSchema,
      })
      .optional(),
  })
  .refine(
    (chain) => chain.tokens.some((token) => token.key === chain.preferredToken),
    "Missing preferred token",
  )
// Historical purchase snapshots lack icons; current configuration must supply them.
const configuredChainSchema = gachaEvmChainSchema.and(
  z.object({ icon: gachaEvmIconSchema }),
)
export type GachaEvmChain = z.infer<typeof gachaEvmChainSchema>
export type GachaEnvironment = "production" | "development"
export type GachaNetworkId = "solana" | number
export type GachaEvmOperation =
  | "generatePack"
  | "openPack"
  | "buyback"
  | "webhook"
  | "transfer"
  | "portfolio"
export function getGachaEnvironment(baseUrl: string): GachaEnvironment | null {
  try {
    const url = new URL(baseUrl)
    if (url.protocol !== "https:") return null
    if (url.hostname === "gacha.collectorcrypt.com") return "production"
    if (url.hostname === "dev-gacha.collectorcrypt.com") return "development"
  } catch {
    /* Missing configuration fails closed. */
  }
  return null
}
export function getGachaEvmChains(
  environment: GachaEnvironment,
): z.infer<typeof configuredChainSchema>[] {
  return z.array(configuredChainSchema).parse(configuration[environment])
}
export function getGachaPreferredToken(chain: GachaEvmChain) {
  const token = chain.tokens.find((lane) => lane.key === chain.preferredToken)
  if (!token) throw new Error("Missing approved preferred payment token")
  return token
}
const scopeSchema = z.object({
  generatePack: z.boolean(),
  openPack: z.boolean(),
  webhook: z.boolean(),
  buyback: z.boolean(),
})
export const collectorCryptEvmChainsSchema = z.object({
  success: z.literal(true),
  allPaused: z.boolean(),
  globalScopesPaused: scopeSchema,
  chains: z.array(
    z.object({
      chainId: z.number().int(),
      ready: z.boolean(),
      paused: z.boolean(),
      scopesPaused: scopeSchema,
      paymentContract: gachaEvmAddressSchema.nullable(),
      vaultContract: gachaEvmAddressSchema.nullable(),
      cardContract: gachaEvmAddressSchema.nullable(),
      mintGuardContract: gachaEvmAddressSchema.nullable(),
      tokens: z.array(tokenSchema),
    }),
  ),
})
const operationSchema = z.object({
  available: z.boolean(),
  reason: z.enum(["unavailable", "mismatch", "paused"]).nullable(),
})
export const gachaEvmReadinessSchema = z.object({
  environment: z.enum(["production", "development"]).nullable(),
  chains: z.array(
    gachaEvmChainSchema.and(
      z.object({
        operations: z.object({
          generatePack: operationSchema,
          openPack: operationSchema,
          buyback: operationSchema,
          webhook: operationSchema,
          transfer: operationSchema,
          portfolio: operationSchema,
        }),
      }),
    ),
  ),
})
export type GachaEvmReadiness = z.infer<typeof gachaEvmReadinessSchema>
export type GachaEvmReadyChain = GachaEvmReadiness["chains"][number]

// JSON owns targets. Upstream owns current readiness and pauses, never approved addresses.
export function createGachaEvmReadiness(
  environment: GachaEnvironment | null,
  live: z.infer<typeof collectorCryptEvmChainsSchema> | null,
): GachaEvmReadiness {
  if (!environment) return { environment: null, chains: [] }
  return {
    environment,
    chains: getGachaEvmChains(environment).map((approved) => {
      const current = live?.chains.find(
        (chain) => chain.chainId === approved.chainId,
      )
      const sameAddress = (left: string | null | undefined, right: string) =>
        left?.toLowerCase() === right.toLowerCase()
      const paymentMatches = approved.tokens.every((token) =>
        current?.tokens.some(
          (lane) =>
            lane.key === token.key &&
            lane.symbol === token.symbol &&
            lane.decimals === token.decimals &&
            sameAddress(lane.address, token.address),
        ),
      )
      const cardMatches = sameAddress(
        current?.cardContract,
        approved.cardContract,
      )
      const paymentContractMatches = sameAddress(
        current?.paymentContract,
        approved.paymentContract,
      )
      const mintMatches = sameAddress(
        current?.mintGuardContract,
        approved.mintGuardContract,
      )
      const vaultMatches = sameAddress(
        current?.vaultContract,
        approved.vaultContract,
      )
      const status = (operation: GachaEvmOperation, matches: boolean) => {
        if (!current || !live || !current.ready)
          return { available: false, reason: "unavailable" } satisfies z.infer<
            typeof operationSchema
          >
        if (!matches)
          return { available: false, reason: "mismatch" } satisfies z.infer<
            typeof operationSchema
          >
        const scoped =
          operation === "generatePack" ||
          operation === "openPack" ||
          operation === "buyback" ||
          operation === "webhook"
        if (
          scoped &&
          (live.allPaused ||
            current.paused ||
            live.globalScopesPaused[operation] ||
            current.scopesPaused[operation])
        )
          return { available: false, reason: "paused" } satisfies z.infer<
            typeof operationSchema
          >
        return { available: true, reason: null }
      }
      return {
        ...approved,
        operations: {
          generatePack: status(
            "generatePack",
            paymentMatches &&
              paymentContractMatches &&
              cardMatches &&
              mintMatches,
          ),
          openPack: status(
            "openPack",
            paymentMatches &&
              paymentContractMatches &&
              cardMatches &&
              mintMatches,
          ),
          buyback: status(
            "buyback",
            paymentMatches && cardMatches && vaultMatches,
          ),
          webhook: status("webhook", cardMatches && mintMatches),
          transfer: status("transfer", cardMatches),
          portfolio: status("portfolio", cardMatches),
        },
      }
    }),
  }
}

// Reservation/quote callers must validate only deliberately approved targets, not adopt them.
export function validateGachaEvmTargets(
  chain: GachaEvmChain,
  targets: {
    tokenAddress: string
    tokenDecimals: number
    paymentContract?: string
    vaultContract?: string
    cardContract?: string
    mintGuardContract?: string
  },
) {
  const token = chain.tokens.find(
    (lane) =>
      lane.address.toLowerCase() === targets.tokenAddress.toLowerCase() &&
      lane.decimals === targets.tokenDecimals,
  )
  if (!token) throw new Error("Unapproved payment token")
  for (const key of [
    "paymentContract",
    "vaultContract",
    "cardContract",
    "mintGuardContract",
  ] satisfies Array<keyof GachaEvmChain>) {
    const target = targets[key]
    if (target && target.toLowerCase() !== chain[key].toLowerCase())
      throw new Error(`Unapproved ${key}`)
  }
  return token
}
